import ReactDOM from 'react-dom';
import {
	Marker as MapboxMarker,
	Popup as MapboxPopup,
	Map as MapboxMap,
	PointLike,
	GeoJSONSource,
	MapLayerMouseEvent,
	LngLatLike
} from 'mapbox-gl';
import { MapboxImage, MapboxMarkerData, MarkerInstance } from '../mapbox';
import {
	defaultAnchor,
	defaultTextColor,
	IdType,
	ImageUrl,
	MapboxClusterData,
	MapboxIconOptions,
	PolylineData,
	Popup
} from '../../../types/maps.consts';
import { Feature, Point } from 'geojson';
import { updateImages } from './images-manager';
import _forEach from 'lodash/forEach';
import _isString from 'lodash/isString';
import _isNil from 'lodash/isNil';
import _defaults from 'lodash/defaults';
import _uniqBy from 'lodash/uniqBy';
import { updatePolylines } from './polylines-manager';

type IdsMap = { [key: number]: boolean };

export const markersSourceId = 'imageMarkersSource';
export const markersLayerId = 'unclustered-point';
export const homeMarkerId = 'homeMarkerId';
export const clustersCountLayerId = 'cluster-count';
export const ungroupedMarkersSourceId = 'ungroupedMarkersSource';
export const ungroupedMarkersLayerId = 'ungroupedMarkersLayer';
export const clustersLayerId = 'clusters';
const homeSourceId = 'homeSourceId';
const homeLayerId = 'homeLayerId';
export const markerIcon =
	'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAABmJLR0QA/wD/AP+gvaeTAAAEJUlEQVRogd2aUWiWVRjHf5M5cjpn5obCLoKaVmKlOLwR1orIJImNqWFX3Q4SvRilU5haF8rwzkJ0kBdSFFoYg1CJEYvJyilFRKYxEUErmk3T1dw+L57ndOzz+8573vc7x4/8w8cH2//9n/95zznPc85zPnhAUBFYbzbwCtACPAM8CszR/10DRoCzwJdAH3A9cPslYxHQC9wEcp6fv4CDQGMZ/N6DaqAHmEDMTQFDQBfQjHRwpn4W6d+2Ad8oNwf8A+wBHrrP3v9FI/C9mpkEDgOPpXj+ceBDbIcGgQWBPSZiKfCrGvgJWFaC1nLgnGpdAp4u2Z0nGrGdOA48HEBzLnAC25noI1ONnU7HgcqA2tOBk9hpFnXN9GCnU4iRyMdc4GdtY0cEfUCizgSysEtZE0loQgLADWB+jAZ6kTd1OIZ4Hj7Stt4LLTwbSWBT+IXYemAXcAZ5szeAYWAnUOfxfCN2VGZl8FsUG5A3NOTBbQfGKJ7R/wTaPHS+Vf76DH6L4oCKdiXw2rEJ7lMkk5vM/hzwGTaBtiZobVfu/qymC2FIRZsdnHrsSHQ6eG8p5xowz8FrUd6pVE4T8JuKLnRwdmFHIgnHSA6xC5Vz1dOjF/5W0RoH5yzJo2Zg3vawg1OjnHFPj164paKubHtdOT5Rxpgcc3CqlXPTx+A0HxISacAekgoh56kF9kDnesbsHEZ9BH07YsRci/OCfi/30DOc8w6OyTdBO2IafMrB+Vy/N3nobc57phAW6/c5Dz1vvItMg3ccnDpkCuaQEFsMW5QzCjzi4O1WXncao0lYp6InE3htSLLLISG2BVn8s4DnkREwCfHVBK0B5SbxUqFeGx9HookLrUiyK7ZFGfUwNwfZaU8AtZldF8GwGlntwZ2HJLvTSFgeQ/ZO3bink8FabWsgi9EkmMz9QQzxPBwhea1lxhPY3WvS9CoFtUgCnkIKfFFwGunM67EaAN7UNvojtkEHtjgQA5XAL9rGhkhtABJGTURqiqD/mmpfRqoqUbFXG/skgraZulsjaN+DBdjFuCSg7ovYA5drcxoU+wg/Kv1ErmcVQgO2qrIygF4r0onfiVP4c6Ibe8rz3UUXQhW2iN1Ruq30mIHcQOWAN0rQ6VSNHwhbS04FU++6ghTx0qIOG85fCugrNSqAr9VIT4bnD+mzrgPWfcNS5PrsNrAixXOrsHeJaW66omInYupH/O41aoCL+szGiL5Sowr4juTjsMH72PNGKREvCp5FptgkUucthlVI/hkHnoxvKxt2IG96hMLbjHqkBJrDr9pSNlQCXyFGj/LfX1ZMA77Q//UR/lcXwdGALXrffQ1hjspXiHSlFgNrkHUwCbyM/D5lEgnRL5TRVyZ0IyPwB1ICygFvl9NQVlQAH2PrWflr5n+FGuTGaZDAl5oPLO4Azh0nI57rcR4AAAAASUVORK5CYII=';
export const DEFAULT_ZOOM = 14;
export const DEFAULT_SPEED = 1.5;
const NO_GROUP = '-1';

type AddMarkersParams = {
	markersAsFeatures: Feature[];
	sourceId: string;
	layerId: string;
	beforeLayerId: string;
	map: MapboxMap;
	markersMap: Map<IdType, MarkerInstance>;
	clusterData?: MapboxClusterData;
};

export const updateMarkers = async (
	markers: MapboxMarkerData[] = [],
	markersMap: Map<IdType, MarkerInstance>,
	imagesMap: Map<ImageUrl, MapboxImage>,
	polylines: PolylineData[],
	map: MapboxMap,
	clusterData?: MapboxClusterData
) => {
	const markersIds = {};
	const imageMarkersAsFeatures: Feature[] = [];
	const uniqueImageUrls: { imageUrl: string; options?: MapboxIconOptions }[] = [];

	_forEach(markers, marker => {
		const { id } = marker;
		markersIds[id] = true;

		if (isMarkerImage(marker)) {
			addMarkerAsImageIcon(map, marker, markersMap, id, imageMarkersAsFeatures, uniqueImageUrls);
		} else {
			addOrUpdateMapboxMarker(marker, markersMap, map);
		}
	});

	await updateImages(
		_uniqBy(uniqueImageUrls, item => {
			return item.imageUrl;
		}),
		imagesMap,
		map
	);

	if (imageMarkersAsFeatures.length) {
		addOrUpdateImageMarkers(imageMarkersAsFeatures, markersMap, map, clusterData);
	}
	updatePolylines(map, polylines);

	removeOldMarkers(markersIds, markersMap, map);
};

export const isMarkerImage = (marker: MapboxMarkerData): boolean => {
	return _isString(marker.icon);
};

const addMarkerAsImageIcon = async (
	map: MapboxMap,
	marker: MapboxMarkerData,
	markersMap: Map<IdType, MarkerInstance>,
	id: IdType,
	imageMarkersAsFeatures: Feature[],
	uniqueImageUrls: { imageUrl: string; options?: MapboxIconOptions }[]
) => {
	const feature = convertImageMarkerToFeature(marker);
	const currentMarker = markersMap.get(id);
	const icon = marker.icon as ImageUrl;

	if (currentMarker?.mapboxMarker) {
		removeMarker(id, markersMap, map);
	}

	const markerInstance = {
		id,
		sourceId: id === homeMarkerId ? homeSourceId : markersSourceId,
		layerId: id === homeMarkerId ? homeLayerId : markersLayerId
	};

	markersMap.set(id, markerInstance);

	const { popup } = marker;

	if (popup) {
		createMapboxPopup(id, popup, markersMap);
	}

	imageMarkersAsFeatures.push(feature);
	uniqueImageUrls.push({ imageUrl: icon, options: marker.iconOptions });
};

const addOrUpdateMapboxMarker = (marker: MapboxMarkerData, markersMap: Map<IdType, MarkerInstance>, map: MapboxMap) => {
	const { id, location, popup, color } = marker;
	const anchor = marker.anchor || defaultAnchor;
	const { lng, lat } = location;
	const icon = marker.icon as JSX.Element;

	const currentMarker = markersMap.get(id);

	if (currentMarker && currentMarker.mapboxMarker) {
		// @ts-ignore
		currentMarker.mapboxMarker._anchor = anchor;
		currentMarker.mapboxMarker.setLngLat([lng, lat]);

		if (icon) {
			ReactDOM.render(icon, currentMarker.mapboxMarker.getElement());
		}
	} else {
		let iconElement;

		if (icon) {
			iconElement = document.createElement('div');
			ReactDOM.render(icon, iconElement);
		}

		const mapboxMarker = new MapboxMarker(iconElement, { anchor, color });
		mapboxMarker.setLngLat([lng, lat]).addTo(map);

		const markerInstance: MarkerInstance = {
			id,
			mapboxMarker
		};
		markersMap.set(id, markerInstance);
	}

	const { mapboxMarker } = markersMap.get(id);
	const currentPopup = mapboxMarker.getPopup();

	if (popup) {
		const mapboxPopup = createMapboxPopup(id, popup, markersMap);

		if (!currentPopup) {
			mapboxMarker.setPopup(mapboxPopup);
		}
	} else {
		if (currentPopup) {
			currentPopup.remove();
		}
	}
};

const createMapboxPopup = (id: IdType, popup: Popup, markersMap: Map<IdType, MarkerInstance>): MapboxPopup => {
	const { element, anchor } = popup;
	let offset: PointLike;
	let resultPopup: MapboxPopup;

	const currentPopup = markersMap.get(id).mapboxPopup;

	if (popup.offset) {
		const { x, y } = popup.offset;
		offset = [x, y];
	}

	if (currentPopup) {
		// @ts-ignore
		const { options } = currentPopup;
		options.anchor = anchor;
		options.offset = offset;

		resultPopup = currentPopup;
	} else {
		const mapboxPopup = new MapboxPopup({ closeButton: false, offset, anchor });
		markersMap.get(id).mapboxPopup = mapboxPopup;

		resultPopup = mapboxPopup;
	}

	const popupElement = document.createElement('div');
	ReactDOM.render(element, popupElement);
	resultPopup.setDOMContent(popupElement);

	return resultPopup;
};

export const convertImageMarkerToFeature = (marker: MapboxMarkerData): Feature => {
	const { id, groupId, location, text, color } = marker;
	const { lat, lng } = location;
	const anchor = marker.anchor || defaultAnchor;
	const icon = marker.icon as ImageUrl;
	const textColor = marker.textColor || defaultTextColor;

	const properties = {
		id,
		groupId,
		anchor,
		icon,
		text,
		color,
		textColor
	};

	return {
		type: 'Feature',
		id,
		properties,
		geometry: {
			type: 'Point',
			coordinates: [lng, lat]
		}
	};
};

const addHomeMarker = (map: MapboxMap, homeMarkerAsFeature?: Feature) => {
	if (_isNil(homeMarkerAsFeature)) {
		return;
	}
	if (map.getLayer(homeLayerId)) {
		map.removeLayer(homeLayerId);
	}
	if (map.getSource(homeSourceId)) {
		map.removeSource(homeSourceId);
	}

	const clustersLayer = map.getLayer(clustersLayerId);

	map.addSource(homeSourceId, {
		type: 'geojson',
		data: homeMarkerAsFeature
	});
	map.addLayer(
		{
			id: homeLayerId,
			type: 'symbol',
			source: homeSourceId,
			filter: ['!', ['has', 'point_count']],
			layout: {
				'icon-size': 1,
				'icon-anchor': ['get', 'anchor'],
				'icon-image': ['concat', 'image-', ['get', 'icon']],
				'icon-allow-overlap': true
			}
		},
		clustersLayer?.id
	);
};

const addOrUpdateImageMarkers = (
	markersAsFeatures: Feature[],
	markersMap: Map<IdType, MarkerInstance>,
	map: MapboxMap,
	clusterData?: MapboxClusterData
) => {
	clusterData = _defaults(clusterData, {
		shouldCluster: false,
		clusterBelowZoom: 14,
		clusterMinPoints: 5,
		clusterRadius: 50
	});

	const homeMarkerAsFeature = markersAsFeatures.filter(feature => feature.id === homeMarkerId)?.[0];
	const notHomeMarkersAsFeatures = [];
	const ungroupedMarkers = [];
	markersAsFeatures.forEach(feature => {
		if (feature.id !== homeMarkerId) {
			if (feature.properties.groupId?.toString() !== NO_GROUP) {
				notHomeMarkersAsFeatures.push(feature);
			} else {
				ungroupedMarkers.push(feature);
			}
		}
	});

	addHomeMarker(map, homeMarkerAsFeature);
	addMarkers({
		markersAsFeatures: notHomeMarkersAsFeatures,
		sourceId: markersSourceId,
		layerId: markersLayerId,
		beforeLayerId: homeLayerId,
		map,
		markersMap,
		clusterData
	});

	addMarkers({
		markersAsFeatures: ungroupedMarkers,
		sourceId: ungroupedMarkersSourceId,
		layerId: ungroupedMarkersLayerId,
		beforeLayerId: markersLayerId,
		map,
		markersMap,
		clusterData
	});

	if (clusterData.shouldCluster) {
		addClusterLayers(markersSourceId, map);
	}
};

const addMarkers = (params: AddMarkersParams) => {
	const { markersMap, map, sourceId, layerId, beforeLayerId, clusterData } = params;
	const { shouldCluster, clusterBelowZoom, clusterMinPoints, clusterRadius } = clusterData;

	const data: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
		type: 'FeatureCollection',
		features: params.markersAsFeatures
	};

	const currentSource = map.getSource(sourceId) as GeoJSONSource;

	if (currentSource) {
		currentSource.setData(data);
	} else {
		map.addSource(sourceId, {
			type: 'geojson',
			data,
			cluster: shouldCluster,
			clusterMaxZoom: clusterBelowZoom,
			clusterMinPoints,
			clusterRadius
		});
	}

	const beforeLayer = map.getLayer(beforeLayerId);

	if (!map.getLayer(layerId)) {
		map.addLayer(
			{
				id: layerId,
				type: 'symbol',
				source: sourceId,
				filter: ['!', ['has', 'point_count']],
				layout: {
					'icon-size': 1,
					'icon-anchor': ['get', 'anchor'],
					'icon-image': ['concat', 'image-', ['get', 'icon']],
					'icon-allow-overlap': true,
					'text-allow-overlap': false,
					'text-font': ['literal', ['Rubik Regular']],
					'text-size': 14,
					'text-field': ['get', 'text'],
					'text-anchor': ['get', 'anchor'],
					'text-offset': [0, -0.8],
					'text-optional': true
				},
				paint: {
					'icon-color': ['get', 'color'],
					'text-color': ['get', 'textColor']
				}
			},
			beforeLayer?.id
		);

		map.on('click', layerId, (event: MapLayerMouseEvent) => {
			if (!event) {
				return;
			}

			const { id } = event.features[0].properties;
			const { coordinates } = event.features[0].geometry as Point;

			if (!markersMap.has(id)) {
				const markerInstance = {
					id,
					sourceId
				};

				markersMap.set(id, markerInstance);
			}

			const { mapboxPopup } = markersMap.get(id);

			if (mapboxPopup) {
				mapboxPopup.setLngLat(coordinates as LngLatLike).addTo(map);
			}
		});
	}
};

const addClusterLayers = (imagesSourceId: string, map: MapboxMap) => {
	if (!map.getLayer(clustersCountLayerId)) {
		map.addLayer({
			id: clustersCountLayerId,
			type: 'symbol',
			source: imagesSourceId,
			filter: ['has', 'point_count'],
			layout: {
				'text-field': '{point_count_abbreviated}',
				'text-size': 12
			}
		});
	}

	if (!map.getLayer(clustersLayerId)) {
		map.addLayer(
			{
				id: clustersLayerId,
				type: 'circle',
				source: imagesSourceId,
				filter: ['has', 'point_count'],
				paint: {
					'circle-color': ['step', ['get', 'point_count'], '#51bbd6', 100, '#f1f075', 750, '#f28cb1'],
					'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40]
				}
			},
			clustersCountLayerId
		);
	}
};

const removeMarker = (markerId: IdType, markersMap: Map<IdType, MarkerInstance>, map: MapboxMap) => {
	const markerInstance = markersMap.get(markerId);
	const mapboxMarker = markerInstance.mapboxMarker;
	const mapboxPopup = markerInstance.mapboxPopup;

	if (mapboxMarker) {
		mapboxMarker.remove();
	}

	if (mapboxPopup) {
		mapboxPopup.remove();
	}

	/* contains only homeIcon and on run marker*/
	if (markersMap.size === 2) {
		removeMarkersAndClustersLayers(map);
	}

	markersMap.delete(markerId);
};

//@ts-ignore
const removeOldMarkers = (markersIds: IdsMap, markersMap: Map<IdType, MarkerInstance>, map: MapboxMap) => {
	for (const key of markersMap.keys()) {
		if (!markersIds[key]) {
			removeMarker(key, markersMap, map);
		}
	}
};

const removeMarkersAndClustersLayers = (map: MapboxMap) => {
	const layerIds = [ungroupedMarkersLayerId, markersLayerId, clustersLayerId, clustersCountLayerId];
	const sourceIds = [ungroupedMarkersSourceId, markersSourceId];

	layerIds.forEach(layerId => {
		if (map.getLayer(layerId)) {
			map.removeLayer(layerId);
		}
	});

	sourceIds.forEach(sourceId => {
		if (map.getSource(sourceId)) {
			map.removeSource(sourceId);
		}
	});
};
